import React, { useEffect } from "react";
import { PlusSignIcon, Delete01Icon, SparklesIcon } from "hugeicons-react";
import { CloseButton } from "../Layout/Buttons/CloseButton";
import { ModeSliderSelector } from "../Mode/ModeSliderSelector";
import '../../Styles/Mode/ModeModal.css';
import { useMode } from "../../contexts/ModeContext";
import type { Mode } from "../../types/mode";

const tuningLabels = {
  clarityLevel: {
    label: "Clarity",
    description: "How directly this mode explains ideas.",
  },
  thoroughnessLevel: {
    label: "Thoroughness",
    description: "How much detail it includes before stopping.",
  },
  interactivityLevel: {
    label: "Interactivity",
    description: "How often it asks questions or checks in.",
  },
} as const;

const modeDescription = (mode: Mode) => {
  const trimmed = mode.instructions.trim();
  if (!trimmed) return "No instructions yet";
  return trimmed.length > 96 ? `${trimmed.slice(0, 93)}...` : trimmed;
};

export const ModeModal: React.FC = () => {
  const {
    isModeModalOpen,
    setIsModeModalOpen,
    handleModeEdit,
    availableModes,
    modeToEdit,
    setModeToEdit,
    handleSaveButtonClick,
    showModeSaveButton,
    startCreateMode,
    handleDeleteMode,
  } = useMode();
  const [removingModeId, setRemovingModeId] = React.useState<string | null>(null);

  useEffect(() => {
    if (!isModeModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsModeModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModeModalOpen, setIsModeModalOpen]);

  if (!isModeModalOpen) {
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setIsModeModalOpen(false);
    }
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>, mode: Mode) => {
    event.stopPropagation();
    setRemovingModeId(mode.id);
    setTimeout(() => {
      handleDeleteMode(mode);
      setRemovingModeId(null);
    }, 400);
  };

  const handleModeItemKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, mode: Mode) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleModeEdit(mode);
    }
  };

  return (
    <div className="mode-modal-overlay" onClick={handleOverlayClick}>
      <section
        className="mode-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mode-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mode-modal-header">
          <div className="mode-modal-title-block">
            <h2 id="mode-modal-title">Modes</h2>
          </div>
          <div className="mode-modal-header-actions">
            <button
              type="button"
              className="mode-modal-header-create"
              onClick={() => startCreateMode()}
              aria-label="Create new mode"
            >
              <PlusSignIcon size={18} />
            </button>
            <CloseButton onClick={() => setIsModeModalOpen(false)} />
          </div>
        </header>

        <div className="mode-modal-content">
          <aside className="mode-modal-sidebar" aria-label="Available modes">
            <label className="mode-modal-mobile-select-label" htmlFor="mode-modal-mobile-select">
              Mode
            </label>
            <select
              id="mode-modal-mobile-select"
              className="mode-modal-mobile-select"
              value={modeToEdit?.id || ""}
              onChange={(event) => {
                const selectedMode = availableModes.find((mode) => mode.id === event.target.value);
                if (selectedMode) {
                  handleModeEdit(selectedMode);
                }
              }}
            >
              <option value="" disabled>
                Choose a mode
              </option>
              {availableModes.map((mode) => (
                <option key={mode.id} value={mode.id}>
                  {mode.name}
                </option>
              ))}
            </select>

            <div className="mode-modal-list">
              {availableModes.map((mode) => (
                <div
                  key={mode.id}
                  role="button"
                  tabIndex={0}
                  className={`mode-modal-mode-item ${mode.id === modeToEdit?.id ? 'active' : ''} ${removingModeId === mode.id ? 'removing' : ''}`}
                  onClick={() => handleModeEdit(mode)}
                  onKeyDown={(event) => handleModeItemKeyDown(event, mode)}
                  aria-pressed={mode.id === modeToEdit?.id}
                >
                  <span className="mode-modal-mode-copy">
                    <span className="mode-modal-mode-name-row">
                      <span className="mode-modal-mode-name">{mode.name}</span>
                    </span>
                    <span className="mode-modal-mode-description">{modeDescription(mode)}</span>
                  </span>
                  {mode.isCustom && (
                    <span
                      className="mode-modal-delete-wrap"
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="mode-modal-delete-button"
                        onClick={(event) => handleDeleteClick(event, mode)}
                        aria-label={`Delete ${mode.name}`}
                      >
                        <Delete01Icon size={18} />
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              className="mode-modal-create-button"
              onClick={() => startCreateMode()}
            >
              <PlusSignIcon size={16} strokeWidth={2.5} />
              <span>New Mode</span>
            </button>
          </aside>

          <main className="mode-modal-editor">
            {modeToEdit ? (
              <>
                <div className="mode-modal-editor-head">
                  <div>
                    <h3>{modeToEdit.name || "Untitled mode"}</h3>
                  </div>
                </div>

                <div className="mode-modal-form">
                  <div className="mode-modal-field">
                    <label className="mode-modal-label" htmlFor="mode-name">
                      Name
                    </label>
                    <p className="mode-modal-help">Short names work best in the chat picker.</p>
                    <textarea
                      id="mode-name"
                      className="mode-modal-textarea mode-modal-name-input"
                      value={modeToEdit?.name || ''}
                      rows={1}
                      disabled={!modeToEdit?.isCustom}
                      onChange={e => {
                        if (modeToEdit) {
                          setModeToEdit({ ...modeToEdit, name: e.target.value });
                        }
                      }}
                    />
                  </div>

                  <div className="mode-modal-field">
                    <label className="mode-modal-label" htmlFor="mode-instructions">
                      Instructions
                    </label>
                    <p className="mode-modal-help">Describe the voice, teaching style, and behavior you want.</p>
                    <textarea
                      id="mode-instructions"
                      className="mode-modal-textarea mode-modal-instructions-input"
                      value={modeToEdit?.instructions || ''}
                      disabled={!modeToEdit?.isCustom}
                      onChange={e => {
                        if (modeToEdit) {
                          setModeToEdit({ ...modeToEdit, instructions: e.target.value });
                        }
                      }}
                    />
                  </div>

                  <div className="mode-modal-tuning-grid" aria-label="Mode tuning">
                    {(Object.keys(tuningLabels) as Array<keyof typeof tuningLabels>).map((key) => (
                      <div className="mode-modal-tuning-item" key={key}>
                        <div className="mode-modal-tuning-header">
                          <label className="mode-modal-label">{tuningLabels[key].label}</label>
                        </div>
                        <p className="mode-modal-help">{tuningLabels[key].description}</p>
                        <ModeSliderSelector
                          value={modeToEdit[key]}
                          disabled={!modeToEdit.isCustom}
                          onChange={(value) => {
                            if (modeToEdit) {
                              setModeToEdit({ ...modeToEdit, [key]: value });
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {showModeSaveButton && (
                  <div className="mode-modal-footer">
                    <span className="mode-modal-unsaved">Unsaved changes</span>
                    <button type="button" className="mode-modal-save-button" onClick={() => handleSaveButtonClick()}>
                      Save Mode
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mode-modal-empty">
                <SparklesIcon size={24} />
                <h3>Choose a mode to edit</h3>
                <p>Select a mode from the library, or create a custom one for a specific class, exam, or study routine.</p>
                <button type="button" className="mode-modal-save-button" onClick={() => startCreateMode()}>
                  Create mode
                </button>
              </div>
            )}
          </main>
        </div>
      </section>
    </div>
  );
};
